import { find, isArray, isObject, map, mapValues, transform } from "lodash";
import initTranslations from "../../app/i18n";
import i18nConfig, { NextJsI18NConfig } from "../../../next-i18next.config";

import { PAGES } from "@/internals/constants/pages";

import { TranslationKey } from "./types";
import { getSiteDomain } from "@/utils/site";

export interface TranslationVariable {
  propertyId: string;
  translations: {
    id: string;
    value: string;
  }[];
}

export type LocaleData = {
  page: string;
  language: string;
  variables?: TranslationVariable[];
  additionalNamespaces?: string[];
};

type TranslatedData<T> = {
  data: T;
};

export const getLanguages = () => {
  const i18n = i18nConfig as NextJsI18NConfig;

  return i18n.locales;
};

export class LocaleDataFetcher<T> {
  page: string;
  language: string;
  variables?: TranslationVariable[];
  additionalNamespaces?: string[];

  constructor({ page, language, variables, additionalNamespaces }: LocaleData) {
    this.page = page || PAGES.NOT_FOUND;
    this.language = language;
    this.variables = variables || [];
    this.additionalNamespaces = additionalNamespaces || [];
  }

  domainFolder = getSiteDomain();

  async getData() {
    const data = (await import(`@/data/domain/${this.domainFolder}/content-types/${this.page}.ts`))
      .default;
    return {
      data,
    };
  }

  async getDataWithTranslations(): Promise<TranslatedData<T>> {
    const namespaces = [this.page, ...this.additionalNamespaces];
    const { t } = await initTranslations({
      locale: this.language,
      namespaces,
    });

    const { data } = await this.getData();
    const translated = this.getTranslations(t, data) as T;

    return {
      data: translated,
    };
  }

  getTranslations = (translateFn: (key: string, options?: any) => string, data: any): any => {
    if (!data || !translateFn) return undefined;

    if (typeof data === "boolean") {
      return data;
    }

    if (isObject(data)) {
      // Keys that should not be translated!
      const NO_TX_KEYS = new Set(["id", "component", "type", "data_qa", "data_testid", "rule"]);

      return isArray(data)
        ? map(data, (dataValue) => this.getTranslations(translateFn, dataValue))
        : mapValues(data, (v, k) => {
            // Special handling for 'name' field - I've removed from the list above's since we have name properties on some other fields
            if (k === "name") {
              if (typeof v === "string") {
                return v;
              }
              return this.getTranslations(translateFn, v);
            }

            if (NO_TX_KEYS.has(k)) return v;
            return this.getTranslations(translateFn, v);
          });
    } else {
      const translationVariables = find(this.variables, { propertyId: data });
      const formatedTranslations =
        translationVariables &&
        transform(
          translationVariables.translations as TranslationKey[],
          (a, b) => {
            return ((a[b.id as keyof TranslationKey] = b.value), a);
          },
          {} as TranslationKey
        );

      if (formatedTranslations) {
        return translateFn(data, formatedTranslations);
      }

      // Try to translate with the keys of the page first
      let translated = translateFn(data, { keySeparator: false, nsSeparator: false });

      // If translation not found and we have additional namespaces, try them
      if (!translated || translated === data) {
        for (const namespace of this.additionalNamespaces) {
          translated = translateFn(data, {
            keySeparator: false,
            nsSeparator: false,
            ns: namespace,
          });
          if (translated && translated !== data) {
            break;
          }
        }
      }

      return translated || data;
    }
  };

  updatePage({ page }: { page: string }) {
    this.page = page || PAGES.NOT_FOUND;
  }

  updateVariables({ variables }: { variables: TranslationVariable[] }) {
    this.variables = variables || [];
  }
}

export async function fetchData<T>(
  page: string,
  lang: string,
  variables?: TranslationVariable[],
  additionalNamespaces?: string[]
): Promise<T> {
  const dataFetcher = new LocaleDataFetcher<T>({
    page: page,
    language: lang,
    variables,
    additionalNamespaces,
  });

  const { data } = await dataFetcher.getDataWithTranslations();

  return data;
}
